import { useCallback, useEffect, useMemo, useState } from 'react';
import { NextRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Checkbox, Select, Typography } from 'antd';
import styled from 'styled-components';

import { AssetCategoryToText } from '@/entities/assets';
import { ContactCategoryToText } from '@/entities/contact';
import { useDelegatingRequests } from '@/entities/delegating-request';
import { PrivateDocumentCategoryToText } from '@/entities/document';
import { Container, Icon, Row } from '@/shared/components';
import { Drawer } from '@/shared/components/drawer';
import { COLORS } from '@/shared/constants/colors';
import { useCreateBeneficiaryMutation } from '@/shared/generated/graphql';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { OmittedBaseDto, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import {
  getPartialForm,
  isAllowedCollection,
  PartialFormAllowedCollection,
} from '@/shared/lib/hooks/getPartialForm';
import { useFormRender } from '@/shared/lib/hooks/use-form-render';
import { useFormSave } from '@/shared/lib/hooks/use-form-save';
import { CollectionName } from '@/shared/lib/sj-orm';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { DocumentCategoryToText } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { useAssistantStore } from '@/shared/store/assistant.store';
import { BFC } from '@/shared/types';
const { Text } = Typography;

const BENEFICIARIES_FORM_TYPE = 'beneficiaries';

type TFormType = TDtoWrapper<OmittedBaseDto, { [TK in keyof OmittedBaseDto]: QuestionType }>;

const COLLECTION_TO_TYPES_TEXT = {
  [CollectionName.ASSETS]: AssetCategoryToText,
  [CollectionName.CONTACTS]: ContactCategoryToText,
  [CollectionName.PRIVATE_DOCUMENTS]: PrivateDocumentCategoryToText,
  [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: DocumentCategoryToText,
};

export const FormDrawerProvider: BFC = ({ children }) => {
  const router = useAppRouter();
  const [formToRender, setFormToRender] = useState<TFormType | null>(null);
  const [collectionName, setCollectionName] = useState<PartialFormAllowedCollection | undefined>();
  const { t } = useTranslation(['assets', 'contacts', 'documents', 'enums']);
  const [typesList, setTypesList] = useState<string[]>([]);
  const [selectedFormType, setSelectedFormType] = useState('');
  const [overrideItemType, setOverrideItemType] = useState<string>();
  const [isSendInvite, setIsSendInvite] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const parseFormParamsFromQuery = useCallback((query: NextRouter['query']) => {
    const collectionToCreate = query.fastAddCollection;

    if (!isAllowedCollection(collectionToCreate)) {
      return;
    }

    const fastAddTypes = query.fastAddTypes;

    if (!Array.isArray(fastAddTypes) && fastAddTypes) {
      setOverrideItemType(fastAddTypes);
    }

    const beneficiaryOverrideType =
      collectionToCreate === CollectionName.BENEFICIARIES ? BENEFICIARIES_FORM_TYPE : undefined;

    const types = beneficiaryOverrideType || fastAddTypes;

    if (collectionToCreate && types) {
      const allowedTypes = Array.isArray(types) ? types : [types];

      const initialType = allowedTypes[0];

      const _formToRender = getPartialForm({
        type: initialType,
        collectionName: collectionToCreate,
        withFastAddFields: true,
        withRequiredFields: true,
      });

      if (!_formToRender) {
        return;
      }

      setCollectionName(collectionToCreate);
      setTypesList(allowedTypes);
      setSelectedFormType(initialType);
      setFormToRender(_formToRender);

      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    parseFormParamsFromQuery(router.query);
  }, [router.query]);

  const clear = useCallback(
    async ({ isFastAdded, isInvited }: { isFastAdded?: boolean; isInvited?: boolean }) => {
      setFormToRender(null);
      setTypesList([]);
      setSelectedFormType('');
      setIsOpen(false);
      setCollectionName(undefined);
      setOverrideItemType(undefined);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fastAdd, fastTrack, fastAddCollection, fastAddTypes, ...query } = router.query;

      await router.replace({ query: { ...query, isFastAdded, isInvited } });
      setIsSendInvite(false);
    },
    [router],
  );

  const close = useCallback(async () => {
    clear({});
  }, [router]);

  const afterSubmit = useCallback(async () => {
    clear({ isFastAdded: true, isInvited: isSendInvite });
  }, [router, isSendInvite]);

  const withDrawer =
    formToRender !== null &&
    collectionName !== undefined &&
    typesList.length !== 0 &&
    selectedFormType !== '';

  const onSelectChange = useCallback(
    (newType: string) => {
      if (!collectionName) {
        return;
      }

      const form = getPartialForm({
        type: newType,
        collectionName,
        withFastAddFields: true,
        withRequiredFields: true,
      });

      setFormToRender(form);
      setSelectedFormType(newType);
    },
    [collectionName],
  );

  const selectListOptions = typesList.map((el) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const translationKey = COLLECTION_TO_TYPES_TEXT[collectionName]?.[el] as string | undefined;

    return {
      label: translationKey ? t(translationKey) : el,
      value: el,
    };
  });

  return (
    <>
      {withDrawer && (
        <Drawer height={'70vh'} placement="bottom" closable={false} onClose={close} open={isOpen}>
          <Row justifyContent="space-between" alignCenter nowrap>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Add item
            </Typography.Title>

            <Icon icon="close" cursorPointer onClick={close} color={COLORS.colorLink} />
          </Row>
          <Container marginTop={24} marginBottom={16}>
            {typesList.length > 1 && (
              <StyledTypeWrapper>
                <span>*Type:</span>
                <Select
                  options={selectListOptions}
                  value={selectedFormType}
                  onSelect={onSelectChange}
                />
              </StyledTypeWrapper>
            )}

            <Form
              // prop key need to update Form component to update initialDtoData every time
              // to update field from keyByCollectionName
              key={selectedFormType}
              form={formToRender}
              afterSubmit={afterSubmit}
              collectionName={collectionName}
              type={overrideItemType || selectedFormType}
              isSendInvite={isSendInvite}
            />
          </Container>

          {collectionName === CollectionName.BENEFICIARIES && (
            <StyledLabel>
              <Checkbox value={isSendInvite} onChange={(e) => setIsSendInvite(e.target.checked)} />
              <StyledText>Send invitation to the Family Member</StyledText>
            </StyledLabel>
          )}
        </Drawer>
      )}
      {children}
    </>
  );
};

const additionalByCollection: Partial<Record<CollectionName, Record<string, unknown>>> = {
  [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: {
    type: DocumentType.PERSONAL_IDENTIFIERS,
    beneficiaryId: 'me',
  },
  [CollectionName.PRIVATE_DOCUMENTS]: {
    type: DocumentType.PRIVATE_DOCUMENTS,
  },
  [CollectionName.BENEFICIARIES]: {},
};

const keyByCollectionName: Partial<Record<CollectionName, string>> = {
  [CollectionName.ASSETS]: 'category',
  [CollectionName.CONTACTS]: 'type',
  [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: 'personalIdentifierType',
  [CollectionName.PRIVATE_DOCUMENTS]: 'privateCategory',
};

const Form = ({
  collectionName,
  form,
  isSendInvite = false,
  type,
  afterSubmit,
}: {
  afterSubmit: () => void;
  collectionName: CollectionName;
  form: TFormType;
  isSendInvite: boolean;
  type: string;
}) => {
  const { requestId } = useAssistantStore();
  const { getDelegatingRequest } = useDelegatingRequests();
  const delegatingRequest = useMemo(() => getDelegatingRequest(requestId), [getDelegatingRequest]);

  const { finish } = useFormSave({
    updateOrSave: 'save',
    collectionName,
    additionalPublicKeys: delegatingRequest?.ownerPublicKey
      ? [delegatingRequest?.ownerPublicKey]
      : undefined,
    initialDtoData: {
      [keyByCollectionName[collectionName] ?? 'type']: type,
      ...(additionalByCollection[collectionName] || {}),
    },
  });

  const [createBeneficiaryMutation] = useCreateBeneficiaryMutation();

  const onSave = async (data: OmittedBaseDto) => {
    let additionalData = {};

    if (collectionName === CollectionName.BENEFICIARIES) {
      const centralDbBeneficiary = await createBeneficiaryMutation({
        variables: {
          email: (data as unknown as { email: string }).email,
          dontSendInviteEmail: !isSendInvite,
        },
      });

      additionalData = {
        centralDbProfileId: centralDbBeneficiary.data?.createBeneficiary.account.id,
        invitationCode: centralDbBeneficiary.data?.createBeneficiary.invitationCode,
        linkedCountryCode: '',
      };
    }

    finish({ ...data, ...additionalData });

    if ('email' in data) {
      localStorage.setItem(`new-family-member-added`, String(data.email));
    }

    afterSubmit();
  };

  const { component, reset } = useFormRender({ form, onSubmit: onSave });

  useEffect(() => {
    reset();
  }, [type]);

  return component;
};

const StyledText = styled(Text)`
  &&& {
    font-family: 'Manrope';
  }
`;

const StyledLabel = styled.label`
  display: flex;
  gap: 8px;
`;

const StyledTypeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;
